/**
 * Which of a listing's images is the generated tyre-on-rim montage
 * (P25009-448). Shared by the browser (the "Illustration" overlay) and the
 * server (the product feeds), so both sides agree on the same image.
 *
 * services/packageReachPublisher.js writes `publicData.renderedImage`:
 *
 *   { kind: 'combination', imageId: '<uuid>', key, sizeLabel }
 *
 * `imageId` is the Sharetribe id of the montage, so the rule follows that
 * exact image even if the seller later reorders the pictures.
 *
 * Listings published before Christofer's feedback (24 Sep) carry
 * `renderedImage` with no `imageId`. Their first image is a generated picture
 * with "Illustration" (or the tyre size) burned into the file. Nothing here
 * adds a second label to those, but the feeds still skip that first image,
 * because text burned into a product image risks rejection by Google and Meta.
 */

const renderedImageOf = publicData => {
  const rendered = publicData && publicData.renderedImage;
  return rendered && typeof rendered === 'object' ? rendered : null;
};

const imageIdOf = image => {
  const id = image && image.id;
  return (id && (id.uuid || id)) || null;
};

/**
 * The montage's image id, or null when the listing has no montage whose id is
 * known. Only a `combination` gets the page overlay: it is the only kind that
 * is an illustration rather than a real photo.
 */
const renderedImageIdOf = publicData => {
  const rendered = renderedImageOf(publicData);
  if (!rendered || rendered.kind !== 'combination' || !rendered.imageId) {
    return null;
  }
  return String(rendered.imageId);
};

/** True when `image` is the montage and should carry the "Illustration" overlay. */
const isRenderedImage = (publicData, image) => {
  const renderedId = renderedImageIdOf(publicData);
  return !!renderedId && imageIdOf(image) === renderedId;
};

/**
 * The listing's images without any generated picture, for the product feeds.
 * On a package this leaves the rim photo first, which is what Google Shopping
 * and Meta must get.
 */
const withoutRenderedImages = (publicData, images) => {
  const list = Array.isArray(images) ? images : [];
  const rendered = renderedImageOf(publicData);
  if (!rendered) {
    return list;
  }
  if (!rendered.imageId) {
    // Legacy listing: the generated picture was always put first.
    return list.slice(1);
  }
  return list.filter(image => imageIdOf(image) !== String(rendered.imageId));
};

module.exports = {
  renderedImageIdOf,
  isRenderedImage,
  withoutRenderedImages,
};
